import { EventForUser } from 'legacy/events/types';
import useTiers from './useTiers';
import { useTranslation } from 'react-i18next';

/**
 * Retrieves addon tiers from an event by filtering the results from useTiers.
 * This ensures we have a single source of truth for tier data.
 */
const useAddonTiers = (event: EventForUser) => {
  const allTiersData = useTiers(event);
  const { t } = useTranslation(['checkout']);

  // Filter all tier-related data to only include addons
  const filterAddonTiers = (tiers: any[]) =>
    tiers?.filter((tier) => tier.type === 'addon') || [];

  const filterSectionsByAddons = (
    sections: any[],
    ticketsBySection: Record<string, any[]>
  ) =>
    sections?.filter((section) => {
      const sectionTiers = ticketsBySection?.[section.id] || [];
      return sectionTiers.some((tier) => tier.type === 'addon');
    }) || [];

  const filterTicketsBySection = (ticketsBySection: Record<string, any[]>) => {
    const filtered: Record<string, any[]> = {};
    Object.entries(ticketsBySection || {}).forEach(([sectionId, tiers]) => {
      const addonTiers = filterAddonTiers(tiers);
      if (addonTiers.length > 0) {
        filtered[sectionId] = addonTiers;
      }
    });
    return filtered;
  };

  const filteredTicketsBySection = filterTicketsBySection(
    allTiersData.ticketsBySection
  );
  const filteredSections = filterSectionsByAddons(
    allTiersData.filteredSections,
    allTiersData.ticketsBySection
  );
  const defaultAddons = filterAddonTiers(
    allTiersData.ticketsBySection?.default || []
  );

  // Filter private tiers to only include addons
  const addonPrivateTiers = filterAddonTiers(allTiersData.privateTiers || []);

  return {
    ...allTiersData,
    data: filterAddonTiers(allTiersData.data),
    sortedTicketTiers: filterAddonTiers(allTiersData.sortedTicketTiers),
    ticketsBySection: filteredTicketsBySection,
    filteredSections,
    privateTiers: addonPrivateTiers,
    menuSections: [
      ...(addonPrivateTiers?.length > 0
        ? [{ id: 'private', name: t('checkout:private') }]
        : []),
      ...filteredSections,
      ...(defaultAddons.length > 0 && filteredSections.length > 0
        ? [{ id: 'uncategorized', name: t('checkout:uncategorized') }]
        : [])
    ]
  };
};

export default useAddonTiers;
